"use client";

import React from "react";
import { observer } from "mobx-react-lite";
import StrongSymbolRow from "./StrongSymbolRow";
import { useStore } from "../services/store";
import { SectorMap } from "./symbols";
import { filterSymbolsByMarketCap } from "./util-symbol-grouping";

function normalizeTopGroups(top) {
  if (Array.isArray(top)) return top;
  if (!top || typeof top !== "object") return [];

  return Object.entries(top).map(([sector, symbols]) => ({
    sector,
    symbols: Array.isArray(symbols) ? symbols : [],
  }));
}

export function getTopSectorSymbolCount(top) {
  return normalizeTopGroups(top).reduce(
    (sum, group) => sum + (group.symbols?.length || 0),
    0,
  );
}

function getTopPercentChange(item) {
  const value = Number(item?.percentChange);
  return Number.isFinite(value) ? value : 0;
}

function sortTopEntries(entries) {
  return [...entries].sort(
    ([, a], [, b]) => getTopPercentChange(b) - getTopPercentChange(a),
  );
}

function getTopGroupStrength(entries) {
  return Math.max(...entries.map(([, item]) => getTopPercentChange(item)));
}

function sortTopGroupsByStrength(grouped) {
  const families = Object.entries(grouped).reduce((result, entry) => {
    const family = entry[0].split("-")[0];
    if (!result[family]) result[family] = [];
    result[family].push(entry);
    return result;
  }, {});

  return Object.entries(families)
    .sort(([familyA, groupsA], [familyB, groupsB]) => {
      const strengthA = Math.max(
        ...groupsA.map(([, entries]) => getTopGroupStrength(entries)),
      );
      const strengthB = Math.max(
        ...groupsB.map(([, entries]) => getTopGroupStrength(entries)),
      );
      return strengthB - strengthA || familyA.localeCompare(familyB);
    })
    .flatMap(([family, groups]) =>
      groups.sort(([sectorA, entriesA], [sectorB, entriesB]) => {
        const parentOrder = Number(sectorB === family) - Number(sectorA === family);
        if (parentOrder) return parentOrder;

        return (
          getTopGroupStrength(entriesB) - getTopGroupStrength(entriesA) ||
          sectorA.localeCompare(sectorB)
        );
      }),
    );
}

function groupTopSymbolsBySector(entries) {
  const grouped = {};
  const seenBySector = {};

  entries.forEach(([symbol, item]) => {
    let matched = false;

    Object.entries(SectorMap).forEach(([sector, sectorSymbols]) => {
      if (!sectorSymbols.includes(symbol)) return;
      matched = true;

      if (!grouped[sector]) {
        grouped[sector] = [];
        seenBySector[sector] = new Set();
      }
      if (!seenBySector[sector].has(symbol)) {
        grouped[sector].push([symbol, item]);
        seenBySector[sector].add(symbol);
      }
    });

    if (!matched) {
      if (!grouped.other) {
        grouped.other = [];
        seenBySector.other = new Set();
      }
      if (!seenBySector.other.has(symbol)) {
        grouped.other.push([symbol, item]);
        seenBySector.other.add(symbol);
      }
    }
  });

  return grouped;
}

function getSectorSpecificity(sector) {
  if (sector === "other") return [-1, -1];
  return [sector.split("-").length, sector.length];
}

function isMoreSpecificSector(candidate, current) {
  const [candidateDepth, candidateLength] = getSectorSpecificity(candidate);
  const [currentDepth, currentLength] = getSectorSpecificity(current);
  return (
    candidateDepth > currentDepth ||
    (candidateDepth === currentDepth && candidateLength > currentLength) ||
    (candidateDepth === currentDepth &&
      candidateLength === currentLength &&
      candidate.localeCompare(current) < 0)
  );
}

function deduplicateTopSectorGroups(grouped) {
  const groupsBySymbols = new Map();

  Object.entries(grouped).forEach(([sector, entries]) => {
    const signature = entries
      .map(([symbol]) => symbol)
      .sort()
      .join("|");
    const existing = groupsBySymbols.get(signature);

    if (!existing || isMoreSpecificSector(sector, existing[0])) {
      groupsBySymbols.set(signature, [sector, entries]);
    }
  });

  return Object.fromEntries(groupsBySymbols.values());
}

function SubsectorHeader({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "rgba(255,255,255,0.6)",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: 4,
        paddingLeft: 4,
        borderLeft: "2px solid rgba(255,255,255,0.2)",
      }}
    >
      {children}
    </div>
  );
}

function TopSymbolRow({ sector, item }) {
  return (
    <StrongSymbolRow
      key={`${sector}-${item.symbol}`}
      sym={item.symbol}
      val={{
        sector,
        pattern: item.pattern || "",
        percentChange: item.percentChange,
      }}
      isPos
      colCh={10}
      source="top"
    />
  );
}

export default observer(function TopSectorPanel({ data }) {
  const store = useStore();
  const displayGroups = normalizeTopGroups(data?.top);

  if (!displayGroups.length) {
    return (
      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          color: "#9ca3af",
          padding: "8px 2px",
        }}
      >
        (no top symbols over 5%)
      </div>
    );
  }

  const rawEntries = displayGroups.flatMap(({ symbols }) =>
    (symbols || [])
      .filter((item) => item?.symbol)
      .map((item) => [String(item.symbol).toLowerCase(), item]),
  );
  const filteredEntries = store.isMarketCapFilter
    ? filterSymbolsByMarketCap(rawEntries, store.marketCapMap)
    : rawEntries;
  const groupEntries = sortTopGroupsByStrength(
    deduplicateTopSectorGroups(groupTopSymbolsBySector(filteredEntries)),
  );

  return (
    <div
      className="font-mono text-sm whitespace-pre leading-5 near-white bg-near-black"
      style={{
        marginBottom: 8,
        borderLeft: "3px solid rgba(34,197,94,.9)",
        padding: 8,
        paddingLeft: 12,
        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,
        backgroundColor: "rgba(20,83,45,.3)",
        boxShadow: "0 2px 6px rgba(0,0,0,.25)",
      }}
    >
      {groupEntries.map(([sector, sectorSymbols], index) => (
        <div key={sector}>
          {index > 0 && <div style={{ height: 8 }} />}
          <SubsectorHeader>{sector}</SubsectorHeader>
          {sortTopEntries(sectorSymbols).map(([, item]) => (
            <TopSymbolRow
              key={`${sector}-${item.symbol}`}
              sector={sector}
              item={item}
            />
          ))}
        </div>
      ))}
    </div>
  );
});
